import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import type { UB04PayerRow } from './types';

const ROWS: Array<'A' | 'B' | 'C'> = ['A', 'B', 'C'];

const PATIENT_RELATIONSHIP_OPTIONS = [
  { value: '18', label: '18 — Self' },
  { value: '01', label: '01 — Spouse' },
  { value: '19', label: '19 — Child' },
  { value: 'G8', label: 'G8 — Other relationship' },
];

const emptyRow = (row: 'A' | 'B' | 'C'): UB04PayerRow => ({ row });

export function PayerInsuredSection({
  value,
  onChange,
  disabled = false,
}: {
  value: UB04PayerRow[];
  onChange: (next: UB04PayerRow[]) => void;
  disabled?: boolean;
}) {
  const visibleRowCount = Math.max(
    1,
    ...value.map(payer => ROWS.indexOf(payer.row) + 1)
  );
  const rows = ROWS.slice(0, visibleRowCount).map(
    row => value.find(payer => payer.row === row) ?? emptyRow(row)
  );

  const update = (row: 'A' | 'B' | 'C', patch: Partial<UB04PayerRow>) => {
    const next = rows.map(payer =>
      payer.row === row ? { ...payer, ...patch, row } : payer
    );
    onChange(next);
  };

  const addRow = () => {
    const row = ROWS[rows.length];
    if (row) {
      onChange([...rows, emptyRow(row)]);
    }
  };

  return (
    <section className='space-y-3'>
      <div>
        <h3 className='text-sm font-semibold'>Payers and insured</h3>
        <p className='text-xs text-muted-foreground'>
          FL 50-65 rows A, B, and C.
        </p>
      </div>
      <div className='space-y-3'>
        {rows.map(row => (
          <div
            key={row.row}
            className='grid gap-2 rounded border p-3 md:grid-cols-3 xl:grid-cols-6'
          >
            <div className='text-sm font-medium md:col-span-3 xl:col-span-6'>
              Row {row.row}
            </div>
            <label className='space-y-1 text-xs text-muted-foreground'>
              FL 50 Payer name
              <Input
                inputSize='sm'
                value={row.payer_name ?? ''}
                disabled={disabled}
                onChange={event =>
                  update(row.row, { payer_name: event.target.value })
                }
              />
            </label>
            <label className='space-y-1 text-xs text-muted-foreground'>
              FL 51 Health plan ID
              <Input
                inputSize='sm'
                value={row.health_plan_id ?? ''}
                disabled={disabled}
                onChange={event =>
                  update(row.row, { health_plan_id: event.target.value })
                }
              />
            </label>
            <label className='space-y-1 text-xs text-muted-foreground'>
              FL 52 Release of information
              <Input
                inputSize='sm'
                value={row.release_of_information ?? ''}
                disabled={disabled}
                onChange={event =>
                  update(row.row, {
                    release_of_information: event.target.value,
                  })
                }
              />
            </label>
            <label className='space-y-1 text-xs text-muted-foreground'>
              FL 53 Assignment of benefits
              <Input
                inputSize='sm'
                value={row.assignment_of_benefits ?? ''}
                disabled={disabled}
                onChange={event =>
                  update(row.row, {
                    assignment_of_benefits: event.target.value,
                  })
                }
              />
            </label>
            <label className='space-y-1 text-xs text-muted-foreground'>
              FL 57 Other provider ID
              <Input
                inputSize='sm'
                value={row.other_provider_id ?? ''}
                disabled={disabled}
                onChange={event =>
                  update(row.row, { other_provider_id: event.target.value })
                }
              />
            </label>
            <label className='space-y-1 text-xs text-muted-foreground'>
              FL 58 Insured name
              <Input
                inputSize='sm'
                value={row.insured_name ?? ''}
                disabled={disabled}
                onChange={event =>
                  update(row.row, { insured_name: event.target.value })
                }
              />
            </label>
            <label className='space-y-1 text-xs text-muted-foreground'>
              FL 59 Patient relationship
              <Select
                value={row.patient_relationship ?? ''}
                disabled={disabled}
                onValueChange={patient_relationship =>
                  update(row.row, { patient_relationship })
                }
              >
                <SelectTrigger
                  selectSize='sm'
                  aria-label='FL 59 Patient relationship'
                >
                  <SelectValue placeholder='Select relationship' />
                </SelectTrigger>
                <SelectContent>
                  {PATIENT_RELATIONSHIP_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
            <label className='space-y-1 text-xs text-muted-foreground'>
              FL 60 Insured ID
              <Input
                inputSize='sm'
                value={row.insured_id ?? ''}
                disabled={disabled}
                onChange={event =>
                  update(row.row, { insured_id: event.target.value })
                }
              />
            </label>
            <label className='space-y-1 text-xs text-muted-foreground'>
              FL 61 Group name
              <Input
                inputSize='sm'
                value={row.group_name ?? ''}
                disabled={disabled}
                onChange={event =>
                  update(row.row, { group_name: event.target.value })
                }
              />
            </label>
            <label className='space-y-1 text-xs text-muted-foreground'>
              FL 62 Group number
              <Input
                inputSize='sm'
                value={row.group_number ?? ''}
                disabled={disabled}
                onChange={event =>
                  update(row.row, { group_number: event.target.value })
                }
              />
            </label>
            <label className='space-y-1 text-xs text-muted-foreground'>
              FL 63 Authorization code
              <Input
                inputSize='sm'
                value={row.authorization_code ?? ''}
                disabled={disabled}
                onChange={event =>
                  update(row.row, { authorization_code: event.target.value })
                }
              />
            </label>
            <label className='space-y-1 text-xs text-muted-foreground'>
              FL 65 Employer name
              <Input
                inputSize='sm'
                value={row.employer_name ?? ''}
                disabled={disabled}
                onChange={event =>
                  update(row.row, { employer_name: event.target.value })
                }
              />
            </label>
          </div>
        ))}
      </div>
      {rows.length < ROWS.length && (
        <Button
          type='button'
          size='sm'
          variant='outline'
          disabled={disabled}
          onClick={addRow}
        >
          Add payer row
        </Button>
      )}
    </section>
  );
}
